const API_ENDPOINT = "/api/careers.php";
const VACANCIES_JSON_ENDPOINT = "/api/data/vacancies.json";
const LOCAL_STORAGE_KEY = "rds-career-vacancies";
const LOCAL_APPLICATIONS_KEY = "rds-career-applications";

const initialResponse = {
  success: true,
  vacancies: [],
  applications: [],
  localFallback: true
};

function getLocalVacancies() {
  try {
    const storedVacancies = JSON.parse(
      window.localStorage.getItem(LOCAL_STORAGE_KEY) || "[]"
    );

    return Array.isArray(storedVacancies) ? storedVacancies : [];
  } catch (error) {
    return [];
  }
}

function setLocalVacancies(vacancies) {
  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(vacancies));
}

function getLocalApplications() {
  try {
    const storedApplications = JSON.parse(
      window.localStorage.getItem(LOCAL_APPLICATIONS_KEY) || "[]"
    );

    return Array.isArray(storedApplications) ? storedApplications : [];
  } catch (error) {
    return [];
  }
}

function setLocalApplications(applications) {
  window.localStorage.setItem(
    LOCAL_APPLICATIONS_KEY,
    JSON.stringify(applications)
  );
}

function mergeById(primaryItems, fallbackItems) {
  const mergedItems = [];
  const seenIds = new Set();

  [...primaryItems, ...fallbackItems].forEach((item) => {
    const itemId = item && item.id ? String(item.id) : "";

    if (!itemId || seenIds.has(itemId)) {
      return;
    }

    seenIds.add(itemId);
    mergedItems.push(item);
  });

  return mergedItems;
}

function cleanText(value) {
  return String(value || "").trim();
}

function cleanResumeFile(resumeFile) {
  if (!resumeFile || typeof resumeFile !== "object") {
    return null;
  }

  const name = cleanText(resumeFile.name);
  const type = cleanText(resumeFile.type);
  const dataUrl = cleanText(resumeFile.dataUrl);
  const size = Number(resumeFile.size) || 0;

  if (!name || type !== "application/pdf" || !dataUrl.startsWith("data:application/pdf;base64,")) {
    return null;
  }

  return {
    name,
    type,
    size,
    dataUrl
  };
}

class ApiUnavailableError extends Error {
  constructor() {
    super("API_UNAVAILABLE");
    this.name = "ApiUnavailableError";
  }
}

function isApiUnavailable(error) {
  return (
    error instanceof ApiUnavailableError ||
    error.name === "TypeError" ||
    error.message === "Failed to fetch"
  );
}

async function parseJsonResponse(response) {
  const responseText = await response.text();

  try {
    return JSON.parse(responseText);
  } catch (error) {
    throw new ApiUnavailableError();
  }
}

async function loadStaticVacancies() {
  const response = await fetch(VACANCIES_JSON_ENDPOINT, {
    cache: "no-store"
  });
  const data = await parseJsonResponse(response);

  if (!response.ok || !Array.isArray(data)) {
    throw new ApiUnavailableError();
  }

  const vacancies = mergeById(getLocalVacancies(), data);
  setLocalVacancies(vacancies);

  return vacancies;
}

function runLocalAdminAction(payload) {
  const vacancies = getLocalVacancies();
  const applications = getLocalApplications();

  if (!cleanText(payload.password)) {
    throw new Error("Enter the admin password.");
  }

  if (payload.action === "verifyAdmin") {
    return {
      ...initialResponse,
      message: "Admin verified in this browser.",
      vacancies,
      applications
    };
  }

  if (payload.action === "add") {
    const vacancy = payload.vacancy || {};
    const requiredFields = [
      "title",
      "department",
      "type",
      "location",
      "experience",
      "description",
      "requirements"
    ];

    const hasMissingField = requiredFields.some(
      (field) => cleanText(vacancy[field]) === ""
    );

    if (hasMissingField) {
      throw new Error("Please fill all required fields.");
    }

    const newVacancy = {
      id: String(Date.now()),
      title: cleanText(vacancy.title),
      department: cleanText(vacancy.department),
      type: cleanText(vacancy.type),
      location: cleanText(vacancy.location),
      experience: cleanText(vacancy.experience),
      description: cleanText(vacancy.description),
      requirements: cleanText(vacancy.requirements),
      deadline: cleanText(vacancy.deadline),
      postedDate: new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    };

    const nextVacancies = [newVacancy, ...vacancies];
    setLocalVacancies(nextVacancies);

    return {
      ...initialResponse,
      message: "Vacancy saved in this browser.",
      vacancies: nextVacancies,
      applications
    };
  }

  if (payload.action === "delete") {
    const nextVacancies = vacancies.filter(
      (vacancy) => String(vacancy.id) !== String(payload.id)
    );

    setLocalVacancies(nextVacancies);

    return {
      ...initialResponse,
      message: "Vacancy deleted from this browser.",
      vacancies: nextVacancies,
      applications
    };
  }

  if (payload.action === "listApplications") {
    return {
      ...initialResponse,
      message: "Applications loaded from this browser.",
      vacancies,
      applications
    };
  }

  throw new Error("Unknown action.");
}

function runLocalApplicationSubmit(payload) {
  const application = payload.application || {};
  const requiredFields = ["jobId", "jobTitle", "name", "email", "phone"];
  const resumeFile = cleanResumeFile(application.resumeFile);

  const hasMissingField = requiredFields.some(
    (field) => cleanText(application[field]) === ""
  );

  if (hasMissingField) {
    throw new Error("Please fill all required fields.");
  }

  if (!resumeFile) {
    throw new Error("Please upload your resume as a PDF file.");
  }

  const applications = getLocalApplications();
  const newApplication = {
    id: String(Date.now()),
    jobId: cleanText(application.jobId),
    jobTitle: cleanText(application.jobTitle),
    name: cleanText(application.name),
    email: cleanText(application.email),
    phone: cleanText(application.phone),
    resumeFile,
    message: cleanText(application.message),
    appliedDate: new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    })
  };

  const nextApplications = [newApplication, ...applications];
  setLocalApplications(nextApplications);

  return {
    ...initialResponse,
    message: "Application submitted successfully.",
    applications: nextApplications
  };
}

export async function loadCareerVacancies() {
  try {
    const response = await fetch(API_ENDPOINT);
    const data = await parseJsonResponse(response);

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Unable to load vacancies.");
    }

    return {
      success: true,
      vacancies: Array.isArray(data.vacancies) ? data.vacancies : [],
      localFallback: false
    };
  } catch (error) {
    try {
      const staticVacancies = await loadStaticVacancies();

      return {
        ...initialResponse,
        vacancies: staticVacancies
      };
    } catch (staticError) {
      // Local React dev cannot execute PHP, so keep the career pages usable.
    }

    return {
      ...initialResponse,
      vacancies: getLocalVacancies()
    };
  }
}

export async function sendCareerAdminRequest(payload) {
  try {
    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
    const data = await parseJsonResponse(response);

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Request failed.");
    }

    return {
      ...data,
      localFallback: false
    };
  } catch (error) {
    if (!isApiUnavailable(error)) {
      throw error;
    }

    return runLocalAdminAction(payload);
  }
}

export async function sendCareerApplication(payload) {
  try {
    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        action: "apply",
        application: payload
      })
    });
    const data = await parseJsonResponse(response);

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Unable to submit application.");
    }

    return {
      ...data,
      localFallback: false
    };
  } catch (error) {
    if (!isApiUnavailable(error)) {
      throw error;
    }

    return runLocalApplicationSubmit({
      application: payload
    });
  }
}
